/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Pressable, Icon, Image } from '@components/atoms'
import { grey, blue } from '@mui/material/colors'
import {
	useAppSelector,
	useAppDispatch,
	configActions,
	ConfigStateType,
} from '@redux/index'
import logoPng from '@public/images/logo/logo_black.png'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

export const DrawerHeader = () => {
	// hook
	const navigate = useNavigate()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const systemName: string = useAppSelector(state => state.config.systemName)
	const systemSubtitle: string = useAppSelector(state => state.config.systemSubtitle)
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)
	const isAdminDrawerHeadAnimated: boolean = useAppSelector(
		state => state.config.isAdminDrawerHeadAnimated,
	)
	const isOpenDrawer: boolean = useAppSelector(state => state.config.isOpenDrawer)

	const onTurnOffAdminClick = React.useCallback(() => {
		reduxDispatch(configActions.MODE('main'))
	}, [])

	const onGoHomeButton = () => {
		const homePath: string = mode === 'admin' ? '/admin/home' : '/main/home'
		navigate(homePath)
	}

	return (
		<View css={style}>
			<motion.div
				initial={
					isAdminDrawerHeadAnimated && mode === 'admin'
						? { opacity: 0, y: '-100%', height: 0 }
						: isAdminDrawerHeadAnimated && mode === 'main'
						? 'offscreen'
						: false
				}
				animate={
					mode === 'admin'
						? { opacity: 1, y: 0, height: '2.75rem' }
						: { opacity: 0, y: '-100%', height: 0 }
				}
				exit={{ opacity: 0, height: 0 }}
				transition={{ duration: 0.5, type: 'spring' }}
				onAnimationComplete={() =>
					reduxDispatch(configActions.IS_ADMIN_DRAWER_HEAD_ANIMATED(false))
				}
			>
				<View className="drawerAdminModeIndicatorView">
					{isOpenDrawer && mode === 'admin' ? <Text>관리자 모드</Text> : null}
					<Pressable
						className={
							isOpenDrawer
								? 'drawerAdminModeIndicatorCloseButton'
								: 'smallDrawerAdminModeIndicatorCloseButton'
						}
						onClick={onTurnOffAdminClick}
					>
						<Icon name="close" className="drawerAdminModeIndicatorCloseIcon" />
					</Pressable>
				</View>
			</motion.div>

			<View className="drawerHeaderInnerView">
				<Pressable className="drawerHeaderButton" onClick={onGoHomeButton}>
					<View className="logoIconView">
						<Image
							src={logoPng}
							className="logo"
							style={
								isOpenDrawer
									? { width: '7rem', height: '7rem', marginRight: 0 }
									: { width: '3.5rem', height: '3.5rem', marginRight: 0 }
							}
						/>
					</View>
					{isOpenDrawer ? (
						<View className="logoTitleView">
							<Text category="h3" className="logoTitle">
								{systemName}
							</Text>
							<Text category="c1" className="logoSubtitle">
								{systemSubtitle}
							</Text>
						</View>
					) : null}
				</Pressable>
			</View>
		</View>
	)
}

const style = css({
	'.drawerAdminModeIndicatorView': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '2.75rem',
		maxHeight: '100%',
		background: blue[700],
		color: '#FFF',

		'.drawerAdminModeIndicatorCloseButton': {
			position: 'absolute',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			width: '1.3rem',
			height: '1.3rem',
			top: '0.7rem',
			left: '0.5rem',
			borderRadius: '50%',

			'.drawerAdminModeIndicatorCloseIcon': {
				fontSize: '14px',
				color: blue[300],
			},

			'&:hover': {
				'.drawerAdminModeIndicatorCloseIcon': {
					fontSize: '20px',
					color: blue[100],
				},
			},
		},

		'.smallDrawerAdminModeIndicatorCloseButton': {
			position: 'absolute',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			width: '1.3rem',
			height: '1.3rem',
			top: '0.7rem',
			left: '0rem',
			right: '0.5rem',
			margin: 'auto',
			borderRadius: '50%',

			'.drawerAdminModeIndicatorCloseIcon': {
				fontSize: '20px',
				color: blue[300],
			},

			'&:hover': {
				'.drawerAdminModeIndicatorCloseIcon': {
					color: blue[100],
				},
			},
		},
	},

	'.drawerHeaderInnerView': {
		'.drawerHeaderButton': {
			minHeight: '10rem',

			'.logoIconView': {
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: 'fit-contents',
			},

			'.logoTitleView': {
				textAlign: 'center',
				color: grey[900],

				'.logoTitle': {
					margin: 0,
					marginBottom: '0.2rem',
					fontFamily: 'audiowideRegular',
				},
				'.logoSubtitle': {
					margin: 0,
					fontSize: 10,
					letterSpacing: -0.2,
					fontWeight: 300,
				},
			},
		},
	},
})
